import React, { useState, useEffect } from 'react';
import promocionService from '../services/promocionService';
import { toast } from 'react-toastify';

const Promociones = () => {
  const [promociones, setPromociones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  // Cargar promociones al montar el componente
  useEffect(() => {
    fetchPromociones();
  }, []);

  // Función para obtener todas las promociones
  const fetchPromociones = async () => {
    try {
      setLoading(true);
      const data = await promocionService.getAllPromociones();
      console.log('Promociones obtenidas:', data);
      const promocionesArray = Array.isArray(data) ? data : [];
      setPromociones(promocionesArray);
      setError(null);
    } catch (err) {
      console.error('Error al cargar promociones:', err);
      setError('No se pudieron cargar las promociones. Por favor, intenta de nuevo más tarde.');
      toast.error('Error al cargar promociones');
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return 'Fecha inválida';
    }
  };

  // Función para eliminar una promoción
  const handleDeletePromocion = async (promocionId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta promoción? Esta acción no se puede deshacer.')) {
      try {
        setProcessingId(promocionId);
        await promocionService.deletePromocion(promocionId);
        
        // Eliminar la promoción del estado local
        setPromociones(prev => prev.filter(promocion => promocion.id !== promocionId));
        
        toast.success('Promoción eliminada exitosamente');
      } catch (error) {
        console.error('Error al eliminar promoción:', error);
        toast.error('Error al eliminar la promoción');
      } finally {
        setProcessingId(null);
      }
    }
  };

  // Función para cambiar el estado de una promoción
  const handleToggleEstado = async (promocionId, estadoActual) => {
    try {
      setProcessingId(promocionId);
      // Si está activa (1), cambiar a inactiva (0), si no está activa, activar (1)
      const nuevoEstado = (estadoActual === 1 || estadoActual === '1') ? 0 : 1;
      
      await promocionService.togglePromocionEstado(promocionId, nuevoEstado);
      
      // Actualizar la promoción en el estado local
      setPromociones(prev => 
        prev.map(promocion => 
          promocion.id === promocionId 
            ? { ...promocion, status: nuevoEstado }
            : promocion
        )
      );
      
      toast.success(`Promoción ${nuevoEstado === 1 ? 'activada' : 'desactivada'} exitosamente`);
    } catch (error) {
      console.error('Error al cambiar estado de promoción:', error);
      toast.error('Error al cambiar el estado de la promoción');
    } finally {
      setProcessingId(null);
    }
  };

  // Función para obtener el color del badge de estado
  const getEstadoBadgeColor = (estado) => {
    switch (estado) {
      case 0:
        return 'bg-red-100 text-red-800'; // Inactiva
      case 1:
     
        return 'bg-green-100 text-green-800'; // Activa
      case 2:
      
        return 'bg-gray-100 text-gray-800'; // Expirada
      case 3:
    
        return 'bg-yellow-100 text-yellow-800'; // Suspendida
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Función para obtener el texto del estado
  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 0:
        return 'Inactiva';
      case 1:     
        return 'Activa';
      case 2:      
        return 'Expirada';
      case 3:    
        return 'Suspendida';
      default:
        return 'Desconocido';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Gestión de Promociones</h2>
        <button
          onClick={fetchPromociones}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-colors"
          disabled={loading}
        >
          {loading ? 'Cargando...' : 'Actualizar'}
        </button>
      </div>

      {/* Estado de carga y error */}
      {loading && <p className="text-gray-600">Cargando promociones...</p>}
      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* Tabla de promociones */}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Título
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Imagen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha Inicio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha Fin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {promociones.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      No hay promociones registradas.
                    </td>
                  </tr>
                ) : (
                  promociones.map((promocion) => (
                    <tr key={promocion.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {promocion.companyName || promocion.businessName || 'Sin empresa'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {promocion.title || 'Sin título'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                        <div className="truncate" title={promocion.description}>
                          {promocion.description || 'Sin descripción'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {promocion.imageUrl ? (
                          <img 
                            src={promocion.imageUrl} 
                            alt="Promoción" 
                            className="h-12 w-12 object-cover rounded-md"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : <div 
                        className="h-12 w-12 bg-gray-200 rounded-md flex items-center justify-center text-xs text-gray-500"
                        style={{ display: promocion.imageUrl ? 'none' : 'flex' }}
                      >
                        Sin imagen
                      </div>}
                        
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(promocion.startDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(promocion.endDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadgeColor(promocion.status)}`}>
                          {getEstadoTexto(promocion.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleToggleEstado(promocion.id, promocion.status)}
                          disabled={processingId === promocion.id || promocion.status === 2 || promocion.status === '2'}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            (promocion.status === 1 || promocion.status === '1')
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          } ${processingId === promocion.id || promocion.status === 2 || promocion.status === '2' ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {processingId === promocion.id 
                            ? 'Procesando...' 
                            : promocion.status === 2 || promocion.status === '2'
                              ? 'Expirada'
                              : (promocion.status === 1 || promocion.status === '1') 
                                ? 'Desactivar' 
                                : 'Activar'
                          }
                        </button>
                        <button
                          onClick={() => handleDeletePromocion(promocion.id)}
                          disabled={processingId === promocion.id}
                          className={`px-3 py-1 rounded text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors ${
                            processingId === promocion.id ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {processingId === promocion.id ? 'Eliminando...' : 'Eliminar'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Promociones;
